import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Truck, Plus, Pencil, Trash2, Scale, AlertTriangle, X, Search } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Pagination } from "@/components/Pagination";
import {
  AggHeader, ResetSampleButton, StatusPill, RiskPill, ExceptionPill, Pill, SortTh, TH_ROW, TD, inr, inrCompact, num,
  fmtDate, STATUSES, STATUS_LABEL, useAggMeta, FieldSelect, EXC_LABEL, hoursLabel,
} from "./aggKit";

const PAGE_SIZE = 25;
const EMPTY = {
  merchant_id: "", carrier_id: "", hub_id: "", dest_city: "", dest_pincode: "", customer_name: "", customer_phone: "",
  payment_mode: "cod", declared_value: "", cod_amount: "", weight_hub_kg: "", weight_carrier_kg: "", freight_charged: "",
};

function F({ label, children, className = "" }) {
  return <div className={`space-y-1 ${className}`}><Label className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</Label>{children}</div>;
}

function DRow({ k, v }) {
  return <div className="flex justify-between gap-3 py-1 border-b border-border/60 text-xs"><span className="text-muted-foreground">{k}</span><span className="font-medium text-right">{v}</span></div>;
}

export function ShipmentDialog({ open, onOpenChange, editing, meta, onSaved }) {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setF(editing ? {
      merchant_id: editing.merchant_id, carrier_id: editing.carrier_id, hub_id: editing.hub_id, dest_city: editing.dest_city,
      dest_pincode: editing.dest_pincode || "", customer_name: editing.customer_name || "", customer_phone: editing.customer_phone || "",
      payment_mode: editing.payment_mode, declared_value: editing.declared_value ?? "", cod_amount: editing.cod_amount ?? "",
      weight_hub_kg: editing.weight_hub_kg ?? "", weight_carrier_kg: editing.weight_carrier_kg ?? "", freight_charged: editing.freight_charged ?? "",
    } : EMPTY);
  }, [open, editing]);
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v?.target ? v.target.value : v }));
  const numOrNull = (v) => (v === "" || v === null || v === undefined ? null : Number(v));

  const save = async () => {
    if (!f.merchant_id || !f.carrier_id || !f.hub_id || !f.dest_city || !f.customer_name || f.declared_value === "" || !f.weight_hub_kg) {
      toast.error("Fill merchant, carrier, hub, destination, customer, value and counter weight");
      return;
    }
    const dest = meta?.destinations?.find((d) => d.city === f.dest_city);
    const body = {
      ...f, declared_value: Number(f.declared_value), weight_hub_kg: Number(f.weight_hub_kg),
      weight_carrier_kg: numOrNull(f.weight_carrier_kg), cod_amount: f.payment_mode === "cod" ? numOrNull(f.cod_amount) : 0,
      freight_charged: numOrNull(f.freight_charged), dest_state: dest?.state || "", dest_region: dest?.region || "",
    };
    setBusy(true);
    try {
      const r = editing ? await api.patch(`/agg/shipments/${editing.id}`, body) : await api.post("/agg/shipments", body);
      toast.success(editing ? "Shipment updated" : `Shipment ${r.data.awb} manifested`);
      onSaved?.(r.data);
      onOpenChange(false);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl rounded-none max-h-[90vh] overflow-y-auto" data-testid="shipment-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? `Edit ${editing.awb}` : "New shipment"}</DialogTitle>
          <DialogDescription>Freight, 3PL cost and franchise commission are worked out from the carrier rate card unless you enter freight yourself.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <F label="Merchant (MSME) *"><FieldSelect testid="sd-merchant" className="w-full" value={f.merchant_id} onChange={set("merchant_id")}><option value="">Select…</option>{meta?.merchants.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}</FieldSelect></F>
          <F label="3PL carrier *"><FieldSelect testid="sd-carrier" className="w-full" value={f.carrier_id} onChange={set("carrier_id")}><option value="">Select…</option>{meta?.carriers.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}</FieldSelect></F>
          <F label="Franchise hub *"><FieldSelect testid="sd-hub" className="w-full" value={f.hub_id} onChange={set("hub_id")}><option value="">Select…</option>{meta?.hubs.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}</FieldSelect></F>
          <F label="Destination city *"><FieldSelect testid="sd-dest" className="w-full" value={f.dest_city} onChange={set("dest_city")}><option value="">Select…</option>{meta?.destinations.map((d) => <option key={d.city} value={d.city}>{d.city} · {d.region}</option>)}</FieldSelect></F>
          <F label="Pincode"><Input className="rounded-none h-9" value={f.dest_pincode} onChange={set("dest_pincode")} data-testid="sd-pincode" /></F>
          <F label="Payment"><FieldSelect testid="sd-payment" className="w-full" value={f.payment_mode} onChange={set("payment_mode")}><option value="cod">COD</option><option value="prepaid">Prepaid</option></FieldSelect></F>
          <F label="Customer name *"><Input className="rounded-none h-9" value={f.customer_name} onChange={set("customer_name")} data-testid="sd-customer" /></F>
          <F label="Customer phone"><Input className="rounded-none h-9" value={f.customer_phone} onChange={set("customer_phone")} data-testid="sd-phone" /></F>
          <F label="Declared value (₹) *"><Input type="number" min="0" className="rounded-none h-9" value={f.declared_value} onChange={set("declared_value")} data-testid="sd-value" /></F>
          {f.payment_mode === "cod" && <F label="COD amount (₹)"><Input type="number" min="0" placeholder="= value" className="rounded-none h-9" value={f.cod_amount} onChange={set("cod_amount")} data-testid="sd-cod" /></F>}
          <F label="Counter weight (kg) *"><Input type="number" min="0" step="0.01" className="rounded-none h-9" value={f.weight_hub_kg} onChange={set("weight_hub_kg")} data-testid="sd-whub" /></F>
          <F label="Carrier manifest wt (kg)"><Input type="number" min="0" step="0.01" className="rounded-none h-9" value={f.weight_carrier_kg} onChange={set("weight_carrier_kg")} data-testid="sd-wcar" /></F>
          <F label="Freight charged (₹)"><Input type="number" min="0" placeholder="auto" className="rounded-none h-9" value={f.freight_charged} onChange={set("freight_charged")} data-testid="sd-freight" /></F>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" className="rounded-none" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="rounded-none" disabled={busy} onClick={save} data-testid="sd-save">{busy ? "Saving…" : editing ? "Save changes" : "Manifest shipment"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function ShipmentDetailSheet({ id, onOpenChange, onChanged, onEdit }) {
  const [s, setS] = useState(null);
  const [status, setStatus] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    if (!id) return;
    api.get(`/agg/shipments/${id}`).then((r) => { setS(r.data); setStatus(r.data.status); setNote(""); }).catch((e) => toast.error(apiErr(e)));
  }, [id]);
  useEffect(() => { setS(null); load(); }, [load]);

  const saveStatus = async () => {
    setBusy(true);
    try {
      await api.patch(`/agg/shipments/${id}`, { status, status_note: note || undefined });
      toast.success("Status updated");
      load(); onChanged?.();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <Sheet open={!!id} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="shipment-detail">
        <SheetHeader><SheetTitle className="font-mono-num">{s?.awb || "Loading…"}</SheetTitle></SheetHeader>
        {s && (
          <div className="mt-3 space-y-4 text-sm">
            <div className="flex items-center gap-2 flex-wrap">
              <StatusPill status={s.status} /><ExceptionPill exc={s.exception} /><RiskPill risk={s.risk} />
              {s.weight?.flag && <Pill className="bg-violet-500/15 text-violet-700 dark:text-violet-300 border-violet-500/30"><Scale className="h-3 w-3" /> weight {s.weight.diff_pct > 0 ? "+" : ""}{s.weight.diff_pct}%</Pill>}
              {s.claim && <Pill className="bg-sky-500/15 text-sky-700 dark:text-sky-300 border-sky-500/30">Claim {s.claim.status}</Pill>}
            </div>
            {s.exception && <div className="border border-rose-500/40 bg-rose-500/10 p-2.5 text-xs flex gap-2"><AlertTriangle className="h-4 w-4 text-rose-500 shrink-0" /><div><b>{EXC_LABEL[s.exception.type]}</b> · last scan {hoursLabel(s.exception.hours_since_scan)} ago{s.exception.sla_breach_hours > 0 && <> · SLA breached by {hoursLabel(s.exception.sla_breach_hours)}</>}</div></div>}
            {s.risk?.reasons?.length > 0 && <div className="text-[11px] text-muted-foreground">RTO risk: {s.risk.reasons.join(" · ")}</div>}
            <div>
              <DRow k="Order" v={s.order_id} />
              <DRow k="Merchant" v={s.merchant_name} />
              <DRow k="Carrier" v={s.carrier_name} />
              <DRow k="First-mile hub" v={s.hub_name} />
              <DRow k="Route" v={`${s.origin_city} → ${s.dest_city} (${s.dest_region}) ${s.dest_pincode || ""}`} />
              <DRow k="Customer" v={`${s.customer_name} ${s.customer_phone || ""}`} />
              <DRow k="Payment" v={s.payment_mode === "cod" ? `COD ${inr(s.cod_amount)} · ${s.cod_status}` : "Prepaid"} />
              <DRow k="Declared value" v={inr(s.declared_value)} />
              <DRow k="Weight counter / carrier" v={`${s.weight_hub_kg} kg / ${s.weight_carrier_kg ?? "—"} kg (chargeable ${s.chargeable_kg} kg)`} />
              <DRow k="Freight + COD fee" v={`${inr(s.freight_charged)} + ${inr(s.cod_fee)} = ${inr(s.revenue)}`} />
              <DRow k="3PL cost / franchise" v={`${inr(s.carrier_cost)} / ${inr(s.franchise_cost)}`} />
              <DRow k="Net profit" v={<span className={s.profit >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600"}>{inr(s.profit, 2)}</span>} />
              <DRow k="Manifested / SLA due" v={`${fmtDate(s.manifested_at, true)} / ${fmtDate(s.expected_delivery_at, true)}`} />
            </div>
            <div className="border border-border p-3 space-y-2">
              <div className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">Update lifecycle stage</div>
              <div className="flex gap-2 flex-wrap">
                <FieldSelect testid="detail-status" value={status} onChange={setStatus} className="flex-1 min-w-[150px]">{STATUSES.map((x) => <option key={x} value={x}>{STATUS_LABEL[x]}</option>)}</FieldSelect>
                <Input className="rounded-none h-9 flex-1 min-w-[150px]" placeholder="Scan note (optional)" value={note} onChange={(e) => setNote(e.target.value)} data-testid="detail-note" />
                <Button className="rounded-none h-9" disabled={busy || status === s.status} onClick={saveStatus} data-testid="detail-save-status">Update</Button>
              </div>
              {onEdit && <Button variant="outline" className="rounded-none h-8 text-xs" onClick={() => onEdit?.(s)} data-testid="detail-edit"><Pencil className="h-3 w-3 mr-1" /> Edit details</Button>}
            </div>
            <div>
              <div className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground mb-2">Tracking timeline</div>
              <ol className="relative border-l border-border ml-1.5 space-y-3">
                {[...(s.status_history || [])].reverse().map((h, i) => (
                  <li key={i} className="ml-4">
                    <span className={`absolute -left-[5px] mt-1 h-2.5 w-2.5 rounded-full ${i === 0 ? "bg-primary" : "bg-muted-foreground/40"}`} />
                    <div className="text-xs font-semibold flex items-center gap-2">{STATUS_LABEL[h.status] || h.status}{h.event && <span className="text-[9px] uppercase text-primary">{h.event}</span>}</div>
                    <div className="text-[11px] text-muted-foreground">{h.note} · {fmtDate(h.at, true)}</div>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

export default function AggShipmentsPage() {
  const [params, setParams] = useSearchParams();
  const meta = useAggMeta();
  const [filters, setFilters] = useState(() => ({
    status: params.get("status") || "", carrier_id: params.get("carrier_id") || "", merchant_id: params.get("merchant_id") || "",
    hub_id: params.get("hub_id") || "", region: params.get("region") || "", payment_mode: "", flag: params.get("flag") || "",
    date_from: "", date_to: "", q: "",
  }));
  const [qInput, setQInput] = useState("");
  const [sort, setSort] = useState({ key: "manifested_at", dir: "desc" });
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0, counts: {}, totals: {} });
  const [loading, setLoading] = useState(true);
  const [dlg, setDlg] = useState({ open: false, editing: null });
  const [detailId, setDetailId] = useState(null);
  const [tick, setTick] = useState(0);

  useEffect(() => { const t = setTimeout(() => setFilters((f) => ({ ...f, q: qInput })), 300); return () => clearTimeout(t); }, [qInput]);
  useEffect(() => { setPage(1); }, [filters, sort]);

  useEffect(() => {
    setLoading(true);
    const p = { ...filters, sort: sort.key, order: sort.dir, page, page_size: PAGE_SIZE };
    Object.keys(p).forEach((k) => p[k] === "" && delete p[k]);
    api.get("/agg/shipments", { params: p })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(apiErr(e)))
      .finally(() => setLoading(false));
  }, [filters, sort, page, tick]);

  const setFilter = (k) => (v) => {
    setFilters((f) => ({ ...f, [k]: v }));
    if (["status", "flag", "carrier_id", "merchant_id", "hub_id", "region"].includes(k)) {
      const np = new URLSearchParams(params);
      if (v) np.set(k, v); else np.delete(k);
      setParams(np, { replace: true });
    }
  };
  const serverSort = useMemo(() => ({
    key: sort.key, dir: sort.dir,
    toggle: (k) => setSort((s) => (s.key === k ? { key: k, dir: s.dir === "asc" ? "desc" : "asc" } : { key: k, dir: "desc" })),
  }), [sort]);
  const activeFilters = Object.entries(filters).filter(([k, v]) => v && k !== "status").length;
  const totalAll = Object.values(data.counts || {}).reduce((a, b) => a + b, 0);

  const del = async (s) => {
    if (!window.confirm(`Delete shipment ${s.awb}? This cannot be undone.`)) return;
    try { await api.delete(`/agg/shipments/${s.id}`); toast.success("Shipment deleted"); setTick((t) => t + 1); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="space-y-4" data-testid="agg-shipments-page">
      <AggHeader
        icon={Truck}
        title="Parcel Flow · Unified Tracking"
        subtitle="Every parcel across every 3PL, from franchise manifest to delivery or RTO."
        actions={<>
          <ResetSampleButton onDone={() => setTick((t) => t + 1)} />
          <button data-testid="new-shipment-btn" onClick={() => setDlg({ open: true, editing: null })} className="h-8 px-3 bg-primary text-primary-foreground text-xs font-bold uppercase tracking-wide inline-flex items-center gap-1.5"><Plus className="h-3.5 w-3.5" /> New shipment</button>
        </>}
      />

      {/* lifecycle tabs */}
      <div className="flex gap-1 overflow-x-auto pb-1" data-testid="status-tabs">
        {[["", "All", totalAll], ...STATUSES.map((s) => [s, STATUS_LABEL[s], data.counts?.[s] || 0])].map(([k, l, c]) => (
          <button key={k || "all"} data-testid={`status-tab-${k || "all"}`} onClick={() => setFilter("status")(k)}
            className={`shrink-0 px-3 py-2 text-xs font-bold uppercase tracking-wide border transition-colors ${filters.status === k ? "bg-[#0B1220] text-white border-[#0B1220] dark:bg-primary dark:border-primary" : "border-border hover:bg-muted"}`}>
            {l} <span className="ml-1 font-mono-num opacity-70">{num(c)}</span>
          </button>
        ))}
      </div>

      {/* filters */}
      <div className="border border-border bg-card p-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-9 gap-2 items-end" data-testid="shipment-filters">
        <div className="col-span-2 sm:col-span-3 lg:col-span-2 relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input className="rounded-none h-9 pl-8 text-xs" placeholder="AWB, order, customer, city, pincode…" value={qInput} onChange={(e) => setQInput(e.target.value)} data-testid="ship-search" />
        </div>
        <FieldSelect testid="f-carrier" value={filters.carrier_id} onChange={setFilter("carrier_id")}><option value="">All carriers</option>{meta?.carriers.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}</FieldSelect>
        <FieldSelect testid="f-merchant" value={filters.merchant_id} onChange={setFilter("merchant_id")}><option value="">All merchants</option>{meta?.merchants.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}</FieldSelect>
        <FieldSelect testid="f-hub" value={filters.hub_id} onChange={setFilter("hub_id")}><option value="">All hubs</option>{meta?.hubs.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}</FieldSelect>
        <FieldSelect testid="f-region" value={filters.region} onChange={setFilter("region")}><option value="">All regions</option>{meta?.regions.map((r) => <option key={r} value={r}>{r}</option>)}</FieldSelect>
        <FieldSelect testid="f-flag" value={filters.flag} onChange={setFilter("flag")}><option value="">Any flag</option><option value="exception">Exceptions / SLA</option><option value="weight">Weight mismatch</option><option value="high_risk">High RTO risk</option></FieldSelect>
        <div className="flex flex-col"><span className="text-[9px] uppercase text-muted-foreground">From</span><Input type="date" className="rounded-none h-9 text-xs" value={filters.date_from} onChange={(e) => setFilter("date_from")(e.target.value)} data-testid="f-from" /></div>
        <div className="flex flex-col"><span className="text-[9px] uppercase text-muted-foreground">To</span><Input type="date" className="rounded-none h-9 text-xs" value={filters.date_to} onChange={(e) => setFilter("date_to")(e.target.value)} data-testid="f-to" /></div>
        {activeFilters > 0 && (
          <button data-testid="f-clear" onClick={() => { setQInput(""); setFilters((f) => ({ ...Object.fromEntries(Object.keys(f).map((k) => [k, ""])), status: f.status })); setParams({}, { replace: true }); }}
            className="h-9 text-xs border border-border inline-flex items-center justify-center gap-1 hover:bg-muted"><X className="h-3 w-3" /> Clear ({activeFilters})</button>
        )}
      </div>

      <div className="flex items-center gap-4 text-xs text-muted-foreground flex-wrap" data-testid="ship-totals">
        <span><b className="text-foreground font-mono-num">{num(data.total)}</b> parcels</span>
        <span>GMV <b className="text-foreground font-mono-num">{inrCompact(data.totals?.declared_value)}</b></span>
        <span>Revenue <b className="text-foreground font-mono-num">{inrCompact(data.totals?.revenue)}</b></span>
        <span>Net profit <b className="text-emerald-600 dark:text-emerald-400 font-mono-num">{inrCompact(data.totals?.profit)}</b></span>
        {loading && <span className="text-primary">Loading…</span>}
      </div>

      <div className="border border-border bg-card overflow-x-auto" data-testid="shipments-table">
        <table className="w-full text-xs">
          <thead className={TH_ROW}>
            <tr>
              <SortTh k="awb" label="AWB / Order" sort={serverSort} />
              <SortTh k="manifested_at" label="Manifested" sort={serverSort} />
              <SortTh k="merchant_name" label="Merchant" sort={serverSort} />
              <SortTh k="carrier_name" label="Carrier" sort={serverSort} />
              <SortTh k="dest_city" label="Hub → Destination" sort={serverSort} />
              <SortTh k="status" label="Stage" sort={serverSort} />
              <SortTh k="cod_amount" label="Payment" sort={serverSort} align="right" />
              <SortTh k="declared_value" label="Value" sort={serverSort} align="right" />
              <SortTh k="weight_carrier_kg" label="Wt ctr / 3PL" sort={serverSort} align="right" />
              <SortTh k="revenue" label="Revenue" sort={serverSort} align="right" />
              <SortTh k="profit" label="Profit" sort={serverSort} align="right" />
              <SortTh k="risk_score" label="RTO risk" sort={serverSort} />
              <SortTh k="expected_delivery_at" label="SLA due" sort={serverSort} />
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {data.items.length === 0 && !loading && <tr><td colSpan={14} className="p-8 text-center text-muted-foreground">No parcels match these filters.</td></tr>}
            {data.items.map((s) => (
              <tr key={s.id} className="hover:bg-muted/40 cursor-pointer" onClick={() => setDetailId(s.id)} data-testid={`ship-row-${s.id}`}>
                <td className={TD}><div className="font-mono-num font-bold">{s.awb}</div><div className="text-[10px] text-muted-foreground">{s.order_id}</div></td>
                <td className={TD}>{fmtDate(s.manifested_at, true)}</td>
                <td className={`${TD} max-w-[160px] truncate`} title={s.merchant_name}>{s.merchant_name}</td>
                <td className={TD}>{s.carrier_name}</td>
                <td className={TD}><div className="text-[10px] text-muted-foreground">{s.hub_name}</div><div>→ {s.dest_city} <span className="text-muted-foreground">· {s.dest_region}</span></div></td>
                <td className={TD}><div className="flex items-center gap-1"><StatusPill status={s.status} /><ExceptionPill exc={s.exception} /></div></td>
                <td className={`${TD} text-right`}>{s.payment_mode === "cod" ? <><div className="font-mono-num">{inr(s.cod_amount)}</div><div className="text-[10px] uppercase text-muted-foreground">COD · {s.cod_status}</div></> : <span className="text-[10px] uppercase text-muted-foreground">Prepaid</span>}</td>
                <td className={`${TD} text-right font-mono-num`}>{inr(s.declared_value)}</td>
                <td className={`${TD} text-right font-mono-num`}>
                  <span className={s.weight?.flag ? "text-violet-600 dark:text-violet-400 font-bold" : ""}>{s.weight_hub_kg} / {s.weight_carrier_kg ?? "—"}</span>
                  {s.weight?.flag && <Scale className="inline h-3 w-3 ml-1 text-violet-500" />}
                </td>
                <td className={`${TD} text-right font-mono-num`}>{inr(s.revenue)}</td>
                <td className={`${TD} text-right font-mono-num ${s.profit < 0 ? "text-rose-600" : "text-emerald-600 dark:text-emerald-400"}`}>{inr(s.profit)}</td>
                <td className={TD}><RiskPill risk={s.risk} /></td>
                <td className={`${TD} ${s.exception?.sla_breach_hours > 0 ? "text-rose-600 dark:text-rose-400 font-bold" : ""}`}>{fmtDate(s.expected_delivery_at)}</td>
                <td className={TD} onClick={(e) => e.stopPropagation()}>
                  <div className="flex gap-1">
                    <button title="Edit" data-testid={`ship-edit-${s.id}`} onClick={() => setDlg({ open: true, editing: s })} className="h-7 w-7 grid place-items-center border border-border hover:bg-muted"><Pencil className="h-3 w-3" /></button>
                    <button title="Delete" data-testid={`ship-del-${s.id}`} onClick={() => del(s)} className="h-7 w-7 grid place-items-center border border-border hover:bg-destructive hover:text-destructive-foreground"><Trash2 className="h-3 w-3" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pagination page={page} total={data.total} pageSize={PAGE_SIZE} onPage={setPage} testid="ship-pagination" />

      <ShipmentDialog open={dlg.open} editing={dlg.editing} meta={meta} onOpenChange={(o) => setDlg((d) => ({ ...d, open: o }))} onSaved={() => setTick((t) => t + 1)} />
      <ShipmentDetailSheet id={detailId} onOpenChange={(o) => !o && setDetailId(null)} onChanged={() => setTick((t) => t + 1)}
        onEdit={(s) => { setDetailId(null); setDlg({ open: true, editing: s }); }} />
    </div>
  );
}
